import { EHAND_API_URL } from '../../comm/config';
import { getUserInfo } from '../../utils';
import { createRequestWithWardIdHeader, rewriteOBJ } from '../commlogic';
import { AxiosInstance } from './axios';

const CACHE_TTL_MS = 5 * 60 * 1000; // Data expires after 5 minutes
const CACHE_SUCCESS_TTL_MS = 60 * 60 * 1000; // Data expires after 60 minutes for successful case
const CLEANUP_INTERVAL_MS = 5 * 60 * 1000; // Run housekeeping every 2 minutes
const MAX_ERROR_RETRIES = 3;

const fileCache = {};

const API_HOST = EHAND_API_URL;

const POST_LIST = {
  getTaskList: '/task/list',
  getTask: '/task/get',
  getDeletedTaskList: '/task/list/deleted',
  getTaskItemList: '/taskItem/list',
  postUpdateItem: '/update/taskItem',
  postCreateComment: '/create/commentitem',
  postDeleteTask: '/delete/task',
  getTagList: '/tag/list',
  postCreateTask: '/create/task',
  postPinTask: '/task/pin',
  getPostdayTaskList: '/task/list/notstarted',
  updateTask: '/update/task',
  getHspTag: '/hspTag/list',
  export: '/task/export',

  // handle the task list
  getNewTaskItemList: '/taskItem/new/list',
  enableHaTeamBulkChecklistItemAPI: '/test/ChecklistItem',

  // patient survey
  getPatientSurveyList: '/patientSurvey/list',
  getPatientSurveyContact: '/patientSurvey/contact',
  genPatientQrCode: '/patientSurvey/qrcode',

  // setting
  addTaskTemplate: '/task/template/add',
  deleteTaskTemplate: '/task/template/delete',
  updateTaskTemplate: '/task/template/edit',

  getTaskRecipients: '/task/sentTo',
  getCheckListConfig: '/task/checklist/config',

  // my draft
  getTaskDraft: '/task/list/draft',
  deleteTaskDraft: '/delete/draft',

  // pin task
  getPinTask: '/task/pin/list',

  // acknowledgement
  getAcknowledgeDetail: '/task/ack/detail',
  getAcknowledgeTask: '/task/list/requireAck',

  // update schedule task
  updateScheduleTask: '/update/schedule/task',
  deleteScheduleTask: '/delete/schedule/task',

  // update share task
  updateShareTask: '/update/file/uploaded/task',

  // update my draft task
  updateMyDraftTask: '/update/draft/task',

  // task upload file
  uploadFileAndCreateTask: '/task/uploadfile',

  // remove task file
  removeTaskFile: '/task/file/remove',
};

const GET_LIST = {
  getFile: '/task/file/get',
  getCommentItem: '/commentitem/list',
  getSrsStaffList: '/srsStaff/list',
  getIndividualUserList: '/sentTo/details',

  // setting
  getSettingAccess: '/settingAccessControl/detail',
  getTaskTemplate: '/task/template/list',
};

const EHANDOVERAPI = {
  POST: POST_LIST,
  GET: GET_LIST,
};

rewriteOBJ(EHANDOVERAPI, API_HOST);

// export acknowledge detail
const ackDetailExport = (data) => {
  return AxiosInstance({
    method: 'post',
    url: `${API_HOST}/task/ack/detail/export`,
    responseType: 'blob',
    data,
    headers: {
      'x-HospitalId': getUserInfo()?.hospId || '',
      'x-WardId': getUserInfo()?.wardId || '',
      isHideLoading: true,
    },
    timeout: 40 * 60 * 1000,
  });
};

const getEhandoverExport = (data) => {
  return AxiosInstance({
    method: 'post',
    url: `${API_HOST}/task/export`,
    responseType: 'blob',
    data,
    headers: {
      'x-HospitalId': getUserInfo()?.hospId || '',
      'x-WardId': getUserInfo()?.wardId || '',
      isHideLoading: true,
    },
    timeout: 40 * 60 * 1000,
  });
};

const getPatientSurveyExport = (data) => {
  return AxiosInstance({
    method: 'post',
    url: `${API_HOST}/patientSurvey/export`,
    responseType: 'blob',
    data,
    headers: {
      'x-HospitalId': getUserInfo()?.hospId || '',
      'x-WardId': getUserInfo()?.wardId || '',
      isHideLoading: true,
    },
    timeout: 40 * 60 * 1000,
  });
};

const runCacheHousekeeping = () => {
  const now = Date.now();
  Object.keys(fileCache).forEach((key) => {
    const entry = fileCache[key];
    
    if (entry.isError) {
      if (entry.fetchedAt && (now - entry.fetchedAt > CACHE_TTL_MS)) {
         delete fileCache[key];
         console.log(`[Cache] Cleaned old error entry: ${key}`);
        return;
      }
    }else if (entry.fetchedAt && (now - entry.fetchedAt > CACHE_SUCCESS_TTL_MS)) {
      delete fileCache[key];
      console.log(`[Cache] Cleaned stale entry: ${key}`);
      return;
    }
  });
};


// 4. Initialize the Cleaner (Run once when the app loads)
// Ensure this doesn't run multiple times if this module is imported elsewhere
if (typeof window !== 'undefined' && !window.fileCacheCleanerInitialized) {
  setInterval(runCacheHousekeeping, CLEANUP_INTERVAL_MS);
  window.fileCacheCleanerInitialized = true;
}

// 5. Enhanced Getter Function
const getFileCache = async (param, headers) => {
  const key = param?.attachmentId ?? "-99999";
  const now = Date.now();
  const existing = fileCache[key];

  // CHECK 1: Is there a valid, non-expired success entry?
  if (existing && !existing.isError) {
    if (now - existing.fetchedAt < CACHE_SUCCESS_TTL_MS) {
      return existing; // Return cached success
    } else {
      // Entry exists but is stale, delete it and proceed to fetch
      delete fileCache[key];
    }
  }

  // CHECK 2: Is there an error entry?
  if (existing && existing.isError) {
      return {}; 
  }

  // FETCH DATA
  try {
    const response = await EHANDOVERAPI.GET.getFile(param, headers);
    
    if (response?.result?.ID && response?.statusCode === "0") {
      // Store Success with Timestamp
      fileCache[key] = {
        ...response,
        fetchedAt: now,
        isError: false,
      };
      return response;
    } else {
      // API returned but indicated failure (non-0 status)
      throw new Error("API returned non-success status");
    }

  } catch (error) {
    fileCache[key] = {
      isError: true,
      fetchedAt: now,
      errorMessage: error.message
    };
    
    return {};
  }
};

// 下面的api需要特殊处理，不管是什么角色Header都加上x-WardId
// setting page
const getSettingView = createRequestWithWardIdHeader('get', API_HOST, '/settingView/detail');
const getSettingTwoWayComm = createRequestWithWardIdHeader('get', API_HOST, '/settingTwoWayComm/detail');
const updateSettingAccess = createRequestWithWardIdHeader('post', API_HOST, '/settingAccessControl/update');
const updateSettingView = createRequestWithWardIdHeader('post', API_HOST, '/settingView/update');
const updateSettingTwoWayComm = createRequestWithWardIdHeader('post', API_HOST, '/settingTwoWayComm/update');
const updateTaskAcknowledgeStatus = createRequestWithWardIdHeader('post', API_HOST, '/task/user/ack');
const getSettingList = createRequestWithWardIdHeader('get', API_HOST, '/setting/list');
const upsertSetting = createRequestWithWardIdHeader('post', API_HOST, '/setting/upsert');



export default {
  ...EHANDOVERAPI.GET,
  ...EHANDOVERAPI.POST,
  getSettingView,
  getSettingTwoWayComm,
  updateSettingAccess,
  updateSettingView,
  updateSettingTwoWayComm,
  updateTaskAcknowledgeStatus,
  getEhandoverExport,
  getPatientSurveyExport,
  ackDetailExport,
  getSettingList,
  getFileCache,
  upsertSetting
};
